import React, {useRef, useState, useEffect, useCallback} from 'react';

import { Language, Video } from '../domain/types';

import { useVideos } from '../providers/VideoContext';

const CLOUDFRONT_DOMAIN = 'https://djud8rv6tyrfq.cloudfront.net/';
const S3_DOMAIN = 'https://alamire.s3.amazonaws.com/';
const S3_DOMAIN_2 = 'https://alamire.s3.eu-west-3.amazonaws.com/';

interface Props {
  video: Video;
  lang: Language;
  email: string;
}

const FeaturedVideo: React.FC<Props> = ({
  video,
  lang,
  email,
}) => {

  const { isAuthenticated, setShowAuthModal } = useVideos();

  let ref = useRef<HTMLVideoElement | null>(null);
  const hasTracked75Ref = useRef(false);
  
  const [src, setSrc] = useState(video.url_en);

  const convertToCloudFront = (src: string) => {
    if (src.startsWith(S3_DOMAIN)) {
      const path = src.split(S3_DOMAIN)[1];
      return `${CLOUDFRONT_DOMAIN}${path}`;

    } else if (src.startsWith(S3_DOMAIN_2)) {
      const path = src.split(S3_DOMAIN_2)[1];
      return `${CLOUDFRONT_DOMAIN}${path}`;
    }

    return src;
  };

  const getSource = useCallback(() => {
    switch (lang) {
      case 'en':
        return video.url_en;

      case 'nl':
        return video.url_nl;

      case 'fr':
        return video.url_fr;

      case 'de':
        return video.url_de;

      default:
        return video.url_en;
    };
  }, [video, lang]);

  useEffect(() => {
    setSrc(convertToCloudFront(getSource()));
  }, [video, lang, getSource]);

  // const play = useCallback(async () => {
  //   try {
  //     await ref.current?.play();
  //   } catch (error) {
  //     console.warn("play failed", error);
  //   }

  //   // @ts-ignore
  //   if (ref.current?.requestFullscreen && !ref.current?.webkitEnterFullscreen) {
  //     await ref.current?.requestFullscreen();
  //   // @ts-ignore
  //   } else if (ref.current?.webkitEnterFullscreen) {
  //     try {
  //       // @ts-ignore
  //       console.log("webkitEnterFullscreen - do nothing");
  //       // await ref.current?.webkitEnterFullscreen();
  //     } catch (error) {
  //       console.warn("webkitEnterFullscreen failed", error);
  //     }
  //   // @ts-ignore
  //   } else if (ref.current?.webkitRequestFullscreen) {
  //   // @ts-ignore
  //     await ref.current?.webkitRequestFullscreen();
  //   // @ts-ignore
  //   } else if (ref.current?.mozRequestFullScreen) {
  //   // @ts-ignore
  //     await ref.current?.mozRequestFullScreen();
  //   // @ts-ignore
  //   } else if (ref.current?.msRequestFullscreen) {
  //   // @ts-ignore
  //     await ref.current?.msRequestFullscreen();
  //   }
  // }, []);

  const onVideoPlay = () => {
    if (!isAuthenticated) {
      ref.current?.pause();
      setShowAuthModal(true);
      return;
    }

    try {
      const gtag = (window as any).gtag;
      if (typeof gtag === 'function') {
        gtag('event', 'alamiretv', {
          action: 'video_play',
          video: src.split('/').pop(),
          viewer: email,
        });
      }
    } catch (error) {
      // ignore error
    }
  };

  const onVideoTimeUpdate = () => {
    if (!isAuthenticated || hasTracked75Ref.current || !ref.current?.duration) {
      return;
    }

    const progress = ref.current.currentTime / ref.current.duration;
    if (progress < 0.75) {
      return;
    }

    hasTracked75Ref.current = true;
    try {
      const gtag = (window as any).gtag;
      if (typeof gtag === 'function') {
        gtag('event', 'alamiretv', {
          action: 'video_progress_75',
          video: src.split('/').pop(),
          viewer: email,
        });
      }
    } catch (error) {
      // ignore error
    }
  };

  useEffect(() => {
    hasTracked75Ref.current = false;

    if (ref.current) {
      ref.current.load();
    }
  }, [lang, src]);

  const onVideoEnded = () => {
    hasTracked75Ref.current = false;
  };

  return (
    <div>
      <video
        id="fullscreen-vid"
        ref={ref}
        key={`${video.title_nl}-${lang}`}
        style={{width: '100%', objectFit: 'contain'}}
        poster={video.placeholder_image}
        controls
        onContextMenu={() => false}
        controlsList="nodownload"
        preload='none'
        onPlay={onVideoPlay}
        onTimeUpdate={onVideoTimeUpdate}
        onEnded={onVideoEnded}
        playsInline
      >
        <source src={src} type="video/mp4"/>
      </video>
    </div>
  );
};

interface InfoProps {
  video: Video;
  lang: Language;
}

export const FeaturedVideoInfo: React.FC<InfoProps> = ({ video, lang }) => {
  const isEnsemble = !!video.producer_ensemble;
  const producer = video.producer_ensemble?.name
    ?? (video as any)[`producer_text_${lang}`]
    ?? null;
  const producerLink = isEnsemble
    ? video.producer_ensemble?.link
    : video.producer_url;
  const description: string | null = (video as any)[`description_${lang}`] ?? null;
  const handwritingTitle: string | null = (video as any)[`handwriting_title_${lang}`] ?? null;

  const showMeta = !!(producer || description || video.handwriting_url);

  if (!showMeta) return null;

  return (
    <div className="featured-info">
      {producer && (
        <div className="featured-info-producer">
          {producerLink ? (
            <a
              href={producerLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {producer}
            </a>
          ) : producer}
        </div>
      )}
      {description && (
        <div
          className="featured-info-description"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
      {video.handwriting_url && handwritingTitle && (
        <a
          className="featured-info-handwriting"
          href={video.handwriting_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {handwritingTitle}
        </a>
      )}
    </div>
  );
};

export default FeaturedVideo;